import { alpha, type Theme } from "@mui/material/styles";
import { blend } from "@mui/system";
import type { DataGridProps, GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import { useMemo } from "react";
import { MOTION, NEUTRAL } from "../../../../theme/tokens";
import type { SearchableColumn } from "./types";

/**
 * Header-cell class applied (via `headerClassName`) to columns flagged `hideHeaderSeparator`.
 * The separator is hidden at rest and revealed on hover — see the `gridSx` rules below. Translated
 * from the column flag in `SystemDataGrid`; callers normally use the flag, not this class directly.
 */
export const HIDE_HEADER_SEPARATOR_CLASS = "system-datagrid-hide-header-separator";

/**
 * Header-cell class SystemDataGrid puts on every column it gives a SortableDataGridHeader. MUI's title content box
 * shrinks to its label, which made only the label sort on click; the `gridSx` rule below stretches it across the
 * title area, so the whole header (bar its padding, the column-menu button and the resize handle) is the target.
 */
export const SORTABLE_HEADER_CLASS = "system-datagrid-sortable-header";

/** Append a class to a column's `headerClassName`, preserving the existing string or function form. */
export function appendHeaderClass<T extends GridValidRowModel>(existing: GridColDef<T>["headerClassName"], cls: string): GridColDef<T>["headerClassName"] {
	if (!existing) return cls;
	if (typeof existing === "function") {
		return (...args: Parameters<typeof existing>) => [existing(...args), cls].filter(Boolean).join(" ");
	}
	return `${existing} ${cls}`;
}

/** Class SystemDataGrid puts on an expanded row's synthetic detail row. */
export const DETAIL_ROW_CLASS = "system-datagrid-detail-row";

/** The row-expansion toggle column's requested width. */
export const EXPAND_FIELD_WIDTH = 44;
/** MUI's default column `minWidth`, which the toggle column's `width` is clamped to. */
const MUI_DEFAULT_COLUMN_MIN_WIDTH_PX = 50;
/** The toggle column as it renders: the band's chevron slot takes this width so the two chevrons share a centre line. */
export const EXPAND_COLUMN_RENDERED_WIDTH_PX = Math.max(EXPAND_FIELD_WIDTH, MUI_DEFAULT_COLUMN_MIN_WIDTH_PX);
/** MUI's cell padding (`.MuiDataGrid-cell { padding: 0 10px }`): the band label's inset, so it starts where the first column's text does. */
export const MUI_CELL_PADDING_X_PX = 10;
/**
 * Row expansion: how far from the row's leading edge the first data column's text starts (the toggle column as it
 * renders, plus MUI's cell padding). Detail content that insets by this lines up under the row's first column, as the
 * band label does.
 */
export const ROW_EXPANSION_CONTENT_INSET_PX = EXPAND_COLUMN_RENDERED_WIDTH_PX + MUI_CELL_PADDING_X_PX;
/** A group band's inline padding (its trailing edge; with row expansion its leading edge is the chevron slot). */
export const GROUP_BAND_PADDING_X_PX = 8;

const HEADER_TINT_OPACITY = 0.4;
const headerTint = (theme: Theme): string => alpha(theme.palette.primary.light, HEADER_TINT_OPACITY);

/** The header tint pre-composited over the paper, for header cells other cells scroll beneath. */
export const opaqueHeaderTint = (theme: Theme): string => blend(theme.palette.background.paper, theme.palette.primary.light, HEADER_TINT_OPACITY);

export const ROW_HOVER_BACKGROUND = NEUTRAL[100];

// Theme overrides have flattened MUI X DataGrid's default header/row separators, so we paint
// them explicitly here. Using neutral tokens directly (instead of `primary.*`) keeps the look
// consistent across tables — callers can still layer their own `sx` on top for accent colors.
//
// Background goes on `.MuiDataGrid-columnHeader` (singular) so every header cell is painted —
// including non-sortable columns like "Actions". The bar-level `.MuiDataGrid-columnHeaders`
// (plural) sits underneath and is fully covered by the per-cell backgrounds the theme paints.
//
// Callback form so any per-property override that needs the user's chosen primary (e.g. the
// no-rows overlay tint) reads the live theme instead of a frozen literal.
const gridSx = (theme: Theme) =>
	({
		border: `1px solid ${NEUTRAL[200]}`,
		// The header tint is translucent. Without an opaque base underneath, body rows scroll
		// *through* the header. Paint background.paper on the top container so the alpha tint
		// composites over solid white instead of letting scrolling content bleed through.
		"& .MuiDataGrid-container--top": {
			bgcolor: theme.palette.background.paper,
		},
		"& .MuiDataGrid-columnHeader": {
			bgcolor: headerTint(theme),
			color: "primary.dark",
			fontWeight: 700,
		},
		// Paint the header bar + the scrollbar gutter slot in the header row so the tint
		// extends past the last column cell when a vertical scrollbar is visible. Without
		// these, the gutter shows the default white background and creates a visible gap.
		"& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
			bgcolor: headerTint(theme),
			borderBottom: "none",
		},
		"& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, color: "primary.dark" },
		[`& .${SORTABLE_HEADER_CLASS} .MuiDataGrid-columnHeaderTitleContainerContent`]: { flex: 1, minWidth: 0 },
		// MUI X paints the column separator icon in `currentColor` which our theme overrides flatten
		// to transparent. Re-color it explicitly so the divider between header cells is visible.
		"& .MuiDataGrid-columnSeparator": {
			opacity: 0.5,
		},
		// MUI hides the separator on a focused (or sibling-of-focused) header so it won't clash with the
		// focus outline. We suppress that outline by default, so keep the separator visible on click instead
		// of letting the divider vanish. The `:not(.hide)` excludes opted-out columns so this (more specific,
		// because of the focus pseudo-class) rule doesn't override their hidden state on focus.
		[`& .MuiDataGrid-columnHeader:focus:not(.${HIDE_HEADER_SEPARATOR_CLASS}) .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader:focus-within:not(.${HIDE_HEADER_SEPARATOR_CLASS}) .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader--siblingFocused:not(.${HIDE_HEADER_SEPARATOR_CLASS}) .MuiDataGrid-columnSeparator`]:
			{
				opacity: 0.5,
			},
		// Per-column opt-out (`hideHeaderSeparator`): hide the divider at rest (and on focus — the focus
		// rule above skips these columns), reveal it on hover. The hover selector is the most specific, so
		// it wins in every state.
		[`& .${HIDE_HEADER_SEPARATOR_CLASS} .MuiDataGrid-columnSeparator`]: {
			opacity: 0,
		},
		[`& .${HIDE_HEADER_SEPARATOR_CLASS}:hover .MuiDataGrid-columnSeparator`]: {
			opacity: 0.5,
		},
		"& .MuiDataGrid-iconSeparator": {
			color: NEUTRAL[500],
			height: "100%",
		},
		"& .MuiDataGrid-cell": {
			borderBottom: `1px solid ${NEUTRAL[150]}`,
		},
		// Vertical centering has to be scoped to the cells that actually need it. In x-data-grid 9.7.0
		// the cell ships `lineHeight: calc(var(--height) - 1px)` (that's how plain text is centered),
		// `overflow: hidden`, `whiteSpace: nowrap`, `textOverflow: ellipsis` and NO `display` — flex is
		// a per-column opt-in (`column.display === "flex"`, class `cell--flex`). Making every cell a
		// flex container turns a renderCell-less column's text into an anonymous flex item, which
		// inherits neither `text-overflow` nor a shrinkable `min-width`, so those columns hard-clip
		// mid-glyph instead of ellipsing (seen on the HRIS mapping grids and Days Off & PTO).
		// A chip or a status pill, on the other hand, IS an element child and does ignore the cell's
		// line-height — so it gets the flex centering, and text-only cells stay on MUI's own path.
		"& .MuiDataGrid-cell:has(> *)": {
			display: "flex",
			alignItems: "center",
		},
		// A flex item's default `min-width: auto` stops a `noWrap` child from ever shrinking below its
		// own text, so it overflows the cell instead of ellipsing inside it.
		"& .MuiDataGrid-cell > *": {
			minWidth: 0,
		},
		"& .MuiDataGrid-row:hover": {
			backgroundColor: ROW_HOVER_BACKGROUND,
		},
		"& .MuiDataGrid-footerContainer": {
			borderTop: `1px solid ${NEUTRAL[200]}`,
		},
		// No-rows overlay tinted in the user's chosen primary so the empty state stays on-brand
		// instead of reading as a flat white panel.
		"& .MuiDataGrid-overlay": {
			backgroundColor: alpha(theme.palette.primary.main, 0.25),
		},
		height: "100%",
		width: "100%",
	}) as const;

/**
 * Cell-focus outline override. MUI's default paints a blue ring around the focused cell;
 * SystemDataGrid suppresses it by default for a list-like feel. Re-enable per-grid via
 * the `showBorderOnCellFocus` prop.
 */
export const cellFocusOffSx = {
	"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
	"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
} as const satisfies DataGridProps["sx"];

/**
 * `hoverCheckboxSelection`: keep the checkbox column's width but hide the checkbox itself until the
 * row is hovered, focused or selected. Opacity rather than `display` so the column never reflows,
 * and the header checkbox (select-all) stays visible so "select everything" is still one click.
 */
export const hoverCheckboxSx = {
	"& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
		opacity: 0,
		transition: `opacity ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
	},
	"@media (prefers-reduced-motion: reduce)": {
		"& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": { transition: "none" },
	},
	"& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-cellCheckbox:focus-within .MuiCheckbox-root, & .MuiDataGrid-cellCheckbox .Mui-checked":
		{
			opacity: 1,
		},
} as const;

/** Compact-density grid defaults — private to SystemDataGrid. */
export const gridBaseProps = {
	density: "compact",
	disableRowSelectionOnClick: true,
	// Built-in DataGrid sorting is OFF; sorting is owned by useMultiSort + SortableDataGridHeader.
	disableColumnSorting: true,
	pageSizeOptions: [25, 50, 100],
	initialState: { pagination: { paginationModel: { pageSize: 50 } } },
	sx: gridSx,
} as const satisfies Partial<DataGridProps>;

type SxLike = DataGridProps["sx"];

/** Layer caller `sx` on top of the base grid sx. Both are forwarded as-is to MUI's sx prop. */
export function mergeSx(base: SxLike, override: SxLike): SxLike {
	if (!override) return base;
	const baseArray = Array.isArray(base) ? base : [base];
	const overrideArray = Array.isArray(override) ? override : [override];
	return [...baseArray, ...overrideArray] as SxLike;
}

/** Pull the searchable string out of a row for one spec. */
export function extractSearchValue<T>(spec: SearchableColumn<T>, row: T): string {
	if (typeof spec === "object" && spec !== null && "getValue" in spec) {
		return spec.getValue(row);
	}
	const v = row[spec as keyof T];
	return v == null ? "" : String(v);
}

/**
 * Stable random skeleton-row widths. Computed once per mount via useMemo so the bars
 * don't reshuffle on each re-render while loading is true. We intentionally exclude
 * `rowCount` and `columnCount` from the dependency array — once the skeleton has been
 * sized to the initial column count it stays that way for the lifetime of the mount,
 * which is what we want here.
 */
export function useSkeletonWidths(rowCount: number, columnCount: number): number[][] {
	// biome-ignore lint/correctness/useExhaustiveDependencies: see jsdoc — widths must stay stable for the mount.
	return useMemo(() => {
		const rows: number[][] = [];
		for (let r = 0; r < rowCount; r++) {
			const row: number[] = [];
			for (let c = 0; c < columnCount; c++) {
				// 40%–90% — random per cell, fixed for the lifetime of this mount.
				row.push(40 + Math.random() * 50);
			}
			rows.push(row);
		}
		return rows;
	}, []);
}
